import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const responseCache = new Map();

export const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin')) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
  );

export const fetchCached = async (path, { ttlMs = 5 * 60 * 1000 } = {}) => {
  const cached = responseCache.get(path);
  const now = Date.now();

  if (cached?.data !== undefined && cached.expiresAt > now) {
    return cached.data;
  }

  if (cached?.promise) {
    return cached.promise;
  }

  const request = api.get(path).then((response) => {
    responseCache.set(path, {
      data: response.data,
      expiresAt: Date.now() + ttlMs,
      promise: null,
    });
    return response.data;
  }).catch((error) => {
    responseCache.delete(path);
    throw error;
  });

  responseCache.set(path, {
    data: cached?.data,
    expiresAt: cached?.expiresAt ?? 0,
    promise: request,
  });

  return request;
};

export const invalidateCached = (...paths) => {
  paths.forEach((path) => responseCache.delete(path));
};

const optimizeRemoteImageUrl = (url, { width, quality = 70 } = {}) => {
  if (!url?.startsWith('https://images.unsplash.com/')) {
    return url;
  }

  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('auto', 'format');
  parsedUrl.searchParams.set('fit', 'crop');
  parsedUrl.searchParams.set('q', String(quality));
  if (width) {
    parsedUrl.searchParams.set('w', String(width));
  }

  return parsedUrl.toString();
};

export const resolveImageUrl = (url, options = {}) => {
  if (!url) return '';
  if (url.startsWith('http')) return optimizeRemoteImageUrl(url, options);
  if (url.startsWith('/')) return url;
  return `${API_BASE}/files/${url}`;
};
